import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'

// Third party libraries
import { produce } from 'immer'

// ** Type Imports
import {
  ApplicationMethodUIConfigType,
  ApplicationStageType,
  ApplicationUIConfigType,
  BulkGenerationUIType,
  CatalogueUIConfigType,
  CheckboxUIConfigType,
  DiscountFormErrorState,
  DiscountFormStateType,
  DiscountGenerationUIConfigType,
  DiscountSetupConfigType,
  DiscountSetupUIConfigType,
  EntitlementUIConfigType,
  FeatherIconType,
  GenerationType,
  InputUIConfigType,
  JourneyLineStepConfigType,
  MethodAndIdentifierUIConfigType,
  OfferValueUIConfigType,
  ChecklistUIConfigType,
  checklistItemsType,
  QuantityDiscountorBlockConfigType,
  TitleSectionUIConfigType,
  DescriptionSectionUIConfigType
} from 'src/lib/types/discounts'
import { CatalogueCategoryType } from 'src/lib/types/common/catalogue'
// ** Util Imports
import InputTextField from 'src/components/common/form-elements/InputTextField'
import {
  getDefaultPaymentModeConcessions,
  getNewPaymentCheckListConfig,
  getQuantityDistributerDefaultConfig,
  getUIConfig
} from 'src/lib/utils/discountHelpers'

// ** Child Imports
import FormTile from 'src/components/common/containers/FormTile'
import FloRadioGroup from 'src/components/common/form-elements/FloRadioGroup'
import FloCheckbox from 'src/components/common/form-elements/FloCheckbox'
import { Divider, ListItem, Typography } from '@mui/material'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { createDeepCopy, disablePlusMinusEKeys, getValueInRange, scrollIntoId } from 'src/lib/utils/helpers'
import { errorMappings } from 'src/lib/utils/validations/discountsValidations'
import { defaultErrorState, defaultQuantityDistributorErrorState } from 'src/assets/DiscountsConfig'
import TextFieldError from 'src/components/common/form-elements/TextFieldError'
import List from '@mui/material/List'
import * as Icon from 'react-feather'
import FileUploaderWithActions from 'src/components/common/form-elements/FileUploaderWithActions'
import QuantityDistributor from './QuantityDistributor'
import BulletedTextEditor from '../../discount-description-editor/BulletedTextEditor'
import { useLocale } from 'src/lib/hooks/useLocal'
import { Info } from 'react-feather'
import { CSVPreview } from 'src/components/common/ui-preview/CSVPreview'
import { ErrorType } from 'src/lib/hooks/useCSVValidator'
import { RadioItemConfigType } from 'src/lib/types/random'
import { regexPatterns } from 'src/lib/utils/validations'

interface DiscountSetupFormProps {
  uiConfig: DiscountSetupUIConfigType
  setFormState: Dispatch<SetStateAction<DiscountFormStateType | undefined>>
  formState: DiscountSetupConfigType | undefined
  isEditMode: boolean
  generationType: GenerationType | undefined
  applicationStage: ApplicationStageType | undefined
  formErrors: DiscountFormErrorState
  setFormErrors: Dispatch<SetStateAction<DiscountFormErrorState>>
}

enum paymentModeInputNameType {
  PERCENTAGE = 'PERCENTAGE',
  UPTO = 'UPTO'
}

const DiscountSetupForm: React.FC<DiscountSetupFormProps> = ({
  uiConfig,
  formState,
  isEditMode,
  setFormState,
  generationType,
  applicationStage,
  formErrors,
  setFormErrors
}) => {
  const { t } = useLocale()
  const {
    getters: { merchant }
  } = useMerchant()
  const { accountId } = merchant || {}

  const { applicationMethodConfig, applicationConfig } = formState || {}
  const {
    getters: { paymentModes }
  } = useMerchant()

  const {
    offerValueConfig,
    entitlementsConfig,
    discountGenerationConfig,
    maxUsesPerOrderCheckboxConfig,
    maxUsesPerOrder,
    discountDescription
  } = applicationConfig || {}

  const {
    applicationMethodUIConfig: applicationMethodUIConfigArray,
    discountGenerationUIConfig: discountGenerationUIConfigArray,
    applicationUIConfig: applicationUIConfigArray,
    offerValueUIConfig: offerValueUIConfigArray,
    entitlementUIConfig: entitlementUIConfigArray,
    titleSectionUIConfig: titleSectionUIConfigArray,
    descriptionSectionUIConfig: descriptionSectionUIConfigArray
  } = uiConfig || {}

  useEffect(() => {
    if (typeof generationType === 'undefined') return
    setFormState(
      produce((draft: any) => {
        draft.setup.applicationConfig.discountGenerationConfig.valueType = generationType
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generationType])

  useEffect(() => {
    if (typeof applicationStage === 'undefined') return
    if (applicationStage === 'PRE_PAYMENT_VALIDATION' && !isEditMode) {
      setFormState(
        produce((draft: any) => {
          draft.setup.applicationConfig.valueType = 'PREPAID'
          draft.setup.applicationMethodConfig.valueType = 'AUTOMATIC'
          if (!Boolean(offerValueConfig?.paymentModeConcessions?.length)) {
            draft.setup.applicationConfig.offerValueConfig.paymentModeConcessions =
              getDefaultPaymentModeConcessions(paymentModes)
          }
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationStage])

  useEffect(() => {
    if (
      applicationStage === 'POST_ORDER_PLACEMENT' &&
      offerValueConfig?.valueType === 'PERCENTAGE' &&
      offerValueConfig?.capCheckboxConfig?.valueType
    ) {
      setFormState(
        produce((draft: any) => {
          ;(draft.setup.applicationConfig.offerValueConfig.capCheckboxConfig.valueType = Boolean('PERCENTAGE_CAPPED')),
            (draft.setup.applicationConfig.offerValueConfig.valueType = 'PERCENTAGE_CAPPED')
        })
      )
    }
  }, [formState])

  const applicationMethodUIConfig: ApplicationMethodUIConfigType = getUIConfig(applicationMethodUIConfigArray, {
    valueType: applicationStage
  })
  const methodAndIdentifierUIConfig: MethodAndIdentifierUIConfigType = getUIConfig(
    applicationMethodUIConfig?.methodAndIdentifierUIConfig,
    { valueType: generationType }
  )
  const codeInputUIConfig: InputUIConfigType = getUIConfig(
    methodAndIdentifierUIConfig?.codeInput,
    applicationMethodConfig
  )

  const titleSectionUIConfig: TitleSectionUIConfigType = getUIConfig(titleSectionUIConfigArray, applicationConfig)

  const descriptionSectionUIConfig: DescriptionSectionUIConfigType = getUIConfig(
    descriptionSectionUIConfigArray,
    applicationConfig
  )

  const applicationUIConfig: ApplicationUIConfigType = getUIConfig(applicationUIConfigArray, {
    valueType: applicationStage
  })

  const maxUsesPerOrderCheckboxUIConfig: CheckboxUIConfigType = getUIConfig(
    applicationUIConfig?.maxUsesPerOrderCheckbox,
    { valueType: generationType }
  )
  const maxUsesPerOrderInputUIConfig: InputUIConfigType = getUIConfig(
    applicationUIConfig?.maxUsesPerOrderInput,
    applicationConfig?.maxUsesPerOrderCheckboxConfig
  )

  const offerValueUIConfig: OfferValueUIConfigType = getUIConfig(offerValueUIConfigArray, applicationConfig)
  const offerValueInputUIConfig: InputUIConfigType = getUIConfig(offerValueUIConfig?.offerValueInput, offerValueConfig)
  const capCheckboxUIConfig: CheckboxUIConfigType = getUIConfig(offerValueUIConfig?.capCheckbox, offerValueConfig)

  const capInputUIConfig: InputUIConfigType = getUIConfig(
    offerValueUIConfig?.capValueInput,
    applicationConfig?.valueType === 'PREPAID'
      ? { valueType: 'PERCENTAGE_CAPPED' }
      : offerValueConfig?.capCheckboxConfig
  )

  const offerQuantityInputUIConfig: InputUIConfigType = offerValueUIConfig?.offerQuantityInput || {}
  const codCheckboxUIConfig: CheckboxUIConfigType = getUIConfig(offerValueUIConfig?.codCheckbox, applicationConfig)
  const newPaymentCheckList = getNewPaymentCheckListConfig(paymentModes)
  const paymentCheckList: ChecklistUIConfigType = getUIConfig(newPaymentCheckList, {
    valueType: offerValueConfig?.paymentModeCoverageType
  })

  const entitlementUIConfig: EntitlementUIConfigType = getUIConfig(entitlementUIConfigArray, applicationConfig)
  const entitledItemsUIConfig: CatalogueUIConfigType = getUIConfig(
    entitlementUIConfig?.entitledItemsUIConfig,
    entitlementsConfig
  )
  const entitledCatalogueCatagory: CatalogueCategoryType = entitledItemsUIConfig?.catalogueType || 'COLLECTION'

  const discountGenerationUIConfig: DiscountGenerationUIConfigType = getUIConfig(discountGenerationUIConfigArray, {
    valueType: generationType
  })
  const bulkGenerationUIConfig: BulkGenerationUIType = getUIConfig(
    discountGenerationUIConfig?.bulkGeneration,
    discountGenerationConfig?.bulkGenerationConfig
  )

  const validatePaymentModeInputFields = (inputName: paymentModeInputNameType, val: number) => {
    const inputFieldErrorStatus = { start: true, end: true }

    if (!offerValueConfig?.paymentModeConcessions?.length || !val) {
      return inputFieldErrorStatus
    }

    if (inputName === paymentModeInputNameType.PERCENTAGE) {
      inputFieldErrorStatus.start = val <= 0
    }

    if (inputName === paymentModeInputNameType.UPTO) {
      inputFieldErrorStatus.end = false
    }

    return inputFieldErrorStatus
  }

  useEffect(() => {
    if (formErrors?.identifier?.status) {
      scrollIntoId('indentifier-input')
    }
  }, [formErrors?.identifier?.status])

  // Methods to render tiles.
  const renderTitleInputSection = () => {
    return (
      <FormTile title={titleSectionUIConfig?.title}>
        <div className='w-[16rem]'>
          <InputTextField
            label={titleSectionUIConfig?.titleInput?.fieldTitle}
            errorStatus={formErrors?.discountTitle?.status}
            handleFocus={() => setFormErrors({ ...formErrors, discountTitle: defaultErrorState })}
            errorMessage={errorMappings?.[formErrors?.discountTitle?.errorCode as keyof typeof errorMappings]}
            textFieldId='desc-title-input'
            inputPlaceholder={titleSectionUIConfig?.titleInput?.placeholder}
            inputType='text'
            inputMargin={'none'}
            isFullWidth={true}
            inputProps={{ maxLength: titleSectionUIConfig?.titleInput?.characterLimit }}
            isRequired={true}
            characterCounter={true}
            maxCharacter={titleSectionUIConfig?.titleInput?.characterLimit}
            inputValue={discountDescription?.discountTitle}
            handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFormState(
                produce((draft: any) => {
                  draft.setup.applicationConfig.discountDescription.discountTitle = event.target.value
                })
              )
            }}
          />
        </div>
      </FormTile>
    )
  }

  const renderDescriptionInputSection = () => {
    return (
      <FormTile title={descriptionSectionUIConfig?.title}>
        <div className='flex flex-col space-y-4'>
          <InputTextField
            label={descriptionSectionUIConfig?.headerInput?.fieldTitle}
            errorStatus={formErrors?.discountHeader?.status}
            handleFocus={() => setFormErrors({ ...formErrors, discountHeader: defaultErrorState })}
            errorMessage={errorMappings?.[formErrors?.discountHeader?.errorCode as keyof typeof errorMappings]}
            textFieldId='desc-header-input'
            inputPlaceholder={descriptionSectionUIConfig?.headerInput?.placeholder}
            inputType='text'
            inputMargin={'none'}
            isFullWidth={true}
            inputProps={{ maxLength: descriptionSectionUIConfig?.headerInput?.characterLimit }}
            isRequired={true}
            characterCounter={true}
            maxCharacter={descriptionSectionUIConfig?.headerInput?.characterLimit}
            inputValue={discountDescription?.discountHeader}
            handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFormState(
                produce((draft: any) => {
                  draft.setup.applicationConfig.discountDescription.discountHeader = event.target.value
                })
              )
            }}
          />
          <InputTextField
            label={descriptionSectionUIConfig?.descriptionInput?.fieldTitle}
            errorStatus={formErrors?.discountDescription?.status}
            handleFocus={() => setFormErrors({ ...formErrors, discountDescription: defaultErrorState })}
            errorMessage={errorMappings?.[formErrors?.discountDescription?.errorCode as keyof typeof errorMappings]}
            textFieldId='desc-description-input'
            inputPlaceholder={descriptionSectionUIConfig?.descriptionInput?.placeholder}
            inputType='text'
            inputMargin={'none'}
            isFullWidth={true}
            inputProps={{ maxLength: descriptionSectionUIConfig?.descriptionInput?.characterLimit }}
            isRequired={true}
            characterCounter={true}
            maxCharacter={descriptionSectionUIConfig?.descriptionInput?.characterLimit}
            inputValue={discountDescription?.discountDescription}
            handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFormState(
                produce((draft: any) => {
                  draft.setup.applicationConfig.discountDescription.discountDescription = event.target.value
                })
              )
            }}
          />
          {/* <InputTextField
            label={descriptionSectionUIConfig?.longDescriptionInput?.fieldTitle}
            errorStatus={formErrors?.discountLongDescription?.status}
            errorMessage={errorMappings?.[formErrors?.discountLongDescription?.errorCode as keyof typeof errorMappings]}
            textFieldId='desc-long-description-input'
            inputPlaceholder={descriptionSectionUIConfig?.longDescriptionInput?.placeholder}
            inputType='text'
            inputMargin={'none'}
            isFullWidth={true}
            inputProps={{ maxLength: descriptionSectionUIConfig?.longDescriptionInput?.characterLimit }}
            isRequired={true}
            characterCounter={true}
            maxCharacter={descriptionSectionUIConfig?.longDescriptionInput?.characterLimit}
            inputValue={discountDescription?.discountLongDescription}
            handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFormState(
                produce((draft: any) => {
                  draft.setup.applicationConfig.discountDescription.discountLongDescription = event.target.value
                })
              )
            }}
          /> */}
          <div className='flex flex-col gap-1'>
            <Typography variant='body2' sx={{ fontWeight: 600, fontSize: 12 }}>
              Details
            </Typography>
            <BulletedTextEditor
              textAreaContent={discountDescription?.discountLongDescription ?? ''}
              setTextAreaContent={(value: any) => {
                setFormState(
                  produce((draft: any) => {
                    draft.setup.applicationConfig.discountDescription.discountLongDescription = value
                  })
                )
              }}
            />
          </div>
        </div>
      </FormTile>
    )
  }

  const renderApplicationMethodTile = () => {
    return (
      <FormTile title={methodAndIdentifierUIConfig?.title}>
        <div className={`flex flex-col w-full gap-3`}>
          <div
            className={`${Boolean(methodAndIdentifierUIConfig?.applicationMethodRadioGroup?.isHidden) ? 'hidden' : ''}`}
          >
            <FloRadioGroup
              selectedOption={applicationMethodConfig?.valueType}
              options={methodAndIdentifierUIConfig?.applicationMethodRadioGroup?.radioItemsUIConfig}
              groupName={'method-of-application'}
              setSelectedOption={(selectedOption: any) => {
                setFormErrors({
                  ...formErrors,
                  identifier: defaultErrorState,
                  offerValue: defaultErrorState,
                  quantityDistributor: defaultQuantityDistributorErrorState,
                  quantityDistributorProduct: defaultQuantityDistributorErrorState
                })
                setFormState(
                  produce((draft: any) => {
                    draft.setup.applicationMethodConfig.valueType = selectedOption
                  })
                )
              }}
            />
          </div>
          <div className='w-1/2'>
            <InputTextField
              errorStatus={formErrors?.identifier?.status}
              errorMessage={errorMappings?.[formErrors?.identifier?.errorCode as keyof typeof errorMappings]}
              inputProps={{ maxLength: 25 }}
              maxCharacter={25}
              characterCounter
              textFieldId='indentifier-input'
              label={codeInputUIConfig?.label}
              inputValue={applicationMethodConfig?.value}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFormState(
                  produce((draft: any) => {
                    draft.setup.applicationMethodConfig.value = event.target.value
                      ?.replace(/\s/g, '') // Remove spaces
                      ?.replace(regexPatterns.hasEmoji, '') // Remove emojis
                      ?.toLocaleUpperCase()
                  })
                )
              }}
              handleFocus={() => setFormErrors({ ...formErrors, identifier: defaultErrorState })}
            />
          </div>
        </div>
      </FormTile>
    )
  }

  const renderApplicationTile = () => {
    return (
      <FormTile title={applicationUIConfig?.title}>
        <div className='flex flex-col w-full gap-3'>
          <FloRadioGroup
            selectedOption={applicationConfig?.valueType}
            options={applicationUIConfig?.applicationRadioGroup?.radioItemsUIConfig}
            groupName={'method-of-application'}
            setSelectedOption={(selectedOption: any) => {
              setFormErrors({
                ...formErrors,
                identifier: defaultErrorState,
                offerValue: defaultErrorState,
                entitledQuantity: defaultErrorState,
                entitledProducts: defaultErrorState,
                quantityDistributor: defaultQuantityDistributorErrorState,
                quantityDistributorProduct: defaultQuantityDistributorErrorState
              })
              setFormState(
                produce((draft: any) => {
                  draft.setup.applicationConfig.valueType = selectedOption
                  draft.setup.applicationConfig.offerValueConfig.valueType =
                    selectedOption === 'BUNDLE' ? 'FLAT' : 'PERCENTAGE'
                  draft.setup.applicationConfig.entitlementsConfig.valueType =
                    selectedOption === 'BXGY' ? 'PRODUCT' : 'ALL'
                  draft.setup.applicationConfig.entitlementsConfig.quantityDistributorConfig = [
                    { quantity: 1, itemsConfig: [] }
                  ]
                  if (selectedOption === 'BUNDLE') {
                    draft.conditions.prerequisitesConfig.quantityDistributorConfig = []
                  }
                })
              )
            }}
          />
          <div
            className={` ${
              maxUsesPerOrderCheckboxUIConfig?.isHidden ||
              applicationConfig?.valueType === 'PREPAID' ||
              applicationConfig?.valueType === 'AMOUNT' ||
              applicationConfig?.valueType === 'SHIPPING'
                ? 'hidden'
                : 'flex flex-col gap-2'
            } `}
          >
            <Divider sx={{ marginLeft: '-16px', marginRight: '-16px' }} />
            <FloCheckbox
              value={Boolean(maxUsesPerOrderCheckboxConfig?.valueType)}
              label={maxUsesPerOrderCheckboxUIConfig?.label}
              setValue={(checked: boolean) => {
                setFormState(
                  produce((draft: any) => {
                    draft.setup.applicationConfig.maxUsesPerOrderCheckboxConfig.valueType = checked
                  })
                )
              }}
            />
            <div className='w-1/2 transition-all duration-200 ease-in-out'>
              {Boolean(maxUsesPerOrderCheckboxConfig?.valueType) ? (
                <InputTextField
                  textFieldId='max-uses-per-order-input'
                  label={maxUsesPerOrderInputUIConfig?.label}
                  inputValue={maxUsesPerOrder}
                  inputProps={{
                    min: 1,
                    max: 999999,
                    onKeyDown: disablePlusMinusEKeys
                  }}
                  handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFormState(
                      produce((draft: any) => {
                        draft.setup.applicationConfig.maxUsesPerOrder = getValueInRange(
                          event.target.value,
                          999999,
                          1,
                          true
                        )
                      })
                    )
                  }}
                  inputType={'number'}
                  inputModeType={'tel'}
                  errorStatus={formErrors?.maxUsesPerOrder?.status}
                  errorMessage={errorMappings?.[formErrors?.maxUsesPerOrder?.errorCode as keyof typeof errorMappings]}
                  handleFocus={() => setFormErrors({ ...formErrors, maxUsesPerOrder: defaultErrorState })}
                />
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      </FormTile>
    )
  }

  const renderOfferInputSection = () => {
    let aggregatedQuantity = 0
    entitlementsConfig?.quantityDistributorConfig?.forEach((blockData: QuantityDiscountorBlockConfigType) => {
      if (blockData?.quantity) aggregatedQuantity += blockData?.quantity
    })
    return (
      <div className='flex w-2/3 gap-2'>
        <div className='w-1/2 transition-all duration-200 ease-in-out'>
          <InputTextField
            errorStatus={formErrors?.offerValue?.status}
            errorMessage={errorMappings?.[formErrors?.offerValue?.errorCode as keyof typeof errorMappings]}
            textFieldId='offer-input'
            label={offerValueInputUIConfig?.label}
            {...(offerValueInputUIConfig?.startIconComponent
              ? { startIcon: offerValueInputUIConfig?.startIconComponent }
              : {})}
            {...(offerValueInputUIConfig?.endIconComponent
              ? { endIcon: offerValueInputUIConfig?.endIconComponent }
              : {})}
            inputValue={offerValueConfig?.value}
            inputProps={{
              min: 0,
              max: offerValueInputUIConfig?.valueType === 'FLAT' ? 999999 : 100,
              onKeyDown: disablePlusMinusEKeys
            }}
            handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFormState(
                produce((draft: any) => {
                  draft.setup.applicationConfig.offerValueConfig.value = getValueInRange(
                    event.target.value,
                    offerValueInputUIConfig?.valueType === 'FLAT' ? 999999 : 100,
                    0
                  )
                })
              )
            }}
            inputType={'number'}
            inputModeType={'tel'}
            handleFocus={() => setFormErrors({ ...formErrors, offerValue: defaultErrorState })}
          />
        </div>
        <div className={`w-1/2`}>
          <div className={`flex ${capInputUIConfig?.isHidden || capCheckboxUIConfig?.isHidden ? 'hidden' : ''}`}>
            <InputTextField
              errorStatus={formErrors?.offerLimit?.status}
              errorMessage={errorMappings?.[formErrors?.offerLimit?.errorCode as keyof typeof errorMappings]}
              textFieldId='cap-input'
              label={capInputUIConfig?.label}
              {...(capInputUIConfig?.startIconComponent ? { startIcon: capInputUIConfig?.startIconComponent } : {})}
              inputValue={offerValueConfig?.cappedValue}
              inputProps={{ min: 0, max: 999999 }}
              handleKeyDown={disablePlusMinusEKeys}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFormState(
                  produce((draft: any) => {
                    draft.setup.applicationConfig.offerValueConfig.cappedValue = getValueInRange(
                      event.target.value,
                      999999,
                      0
                    )
                  })
                )
              }}
              inputType={'number'}
              inputModeType={'tel'}
              handleFocus={() => setFormErrors({ ...formErrors, offerLimit: defaultErrorState })}
            />
          </div>
          <div className={`flex ${offerQuantityInputUIConfig?.isHidden ? 'hidden' : ''}`}>
            <InputTextField
              isDisabled={
                (applicationConfig?.valueType === 'BUNDLE' && entitlementsConfig?.valueType !== 'ALL') ||
                applicationConfig?.valueType === 'BXGY'
              }
              errorStatus={formErrors?.entitledQuantity?.status}
              errorMessage={errorMappings?.[formErrors?.entitledQuantity?.errorCode as keyof typeof errorMappings]}
              textFieldId='offer-quantity-input'
              label={offerQuantityInputUIConfig?.label}
              inputValue={
                applicationConfig?.valueType === 'AMOUNT' ||
                (applicationConfig?.valueType === 'BUNDLE' && entitlementsConfig?.valueType === 'ALL')
                  ? offerValueConfig?.quantity
                  : aggregatedQuantity
              }
              inputProps={{
                min: 0,
                max: 999999,
                onKeyDown: disablePlusMinusEKeys
              }}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFormState(
                  produce((draft: any) => {
                    draft.setup.applicationConfig.offerValueConfig.quantity = getValueInRange(
                      event.target.value,
                      999999,
                      0,
                      true
                    )
                  })
                )
              }}
              inputType={'number'}
              inputModeType={'tel'}
              handleFocus={() => setFormErrors({ ...formErrors, entitledQuantity: defaultErrorState })}
            />
          </div>
        </div>
      </div>
    )
  }

  const renderPaymentOfferChecklist = () => {
    return (
      <List sx={{ padding: 0, marginLeft: 4 }}>
        {paymentCheckList?.checklistItems
          ?.filter((paymentMode: checklistItemsType) => Boolean(paymentMode))
          .map((paymentMode: checklistItemsType, index: number) => {
            const paymentModeCheckbox = getUIConfig(paymentMode?.paymentModeCheckbox, { valueType: true })
            const paymentModeOfferInput = getUIConfig(paymentMode?.paymentModeOfferInput, {
              valueType: offerValueConfig?.valueType
            })
            const paymentModeCapInput = getUIConfig(paymentMode?.paymentModeCapInput, {
              valueType: offerValueConfig?.valueType
            })
            const id = paymentMode.valueType
            const paymentConfigConcessions = offerValueConfig?.paymentModeConcessions?.find(
              item => item.valueType === id
            )
            return (
              <ListItem key={`${paymentMode?.valueType}_${index}`} sx={{ padding: 0.5 }}>
                <div className={`${typeof paymentModeCheckbox === 'undefined' ? 'hidden' : ''} w-full`}>
                  <FloCheckbox
                    value={Boolean(paymentConfigConcessions?.paymentModeCheckboxConfig?.valueType)}
                    label={paymentModeCheckbox?.label}
                    setValue={(checked: boolean) => {
                      setFormErrors({ ...formErrors, paymentModeChecklist: defaultErrorState })
                      setFormState(
                        produce((draft: any) => {
                          draft.setup.applicationConfig.offerValueConfig.paymentModeConcessions.find(
                            (item: checklistItemsType) => item.valueType === id
                          ).paymentModeCheckboxConfig.valueType = checked
                        })
                      )
                    }}
                  />
                  <div
                    className={`${
                      !Boolean(paymentConfigConcessions?.paymentModeCheckboxConfig?.valueType) ? 'hidden' : ''
                    } flex items-center gap-2`}
                  >
                    <div
                      className={`flex w-full ${
                        !(
                          offerValueConfig?.valueType === 'FREEBIE' &&
                          offerValueConfig?.paymentModeCoverageType === 'SPECIFIC'
                        )
                          ? offerValueConfig?.valueType === 'PERCENTAGE_CAPPED'
                            ? 'w-2/3'
                            : 'w-1/3'
                          : 'hidden'
                      }`}
                    >
                      <InputTextField
                        errorMessage={
                          errorMappings?.[
                            formErrors?.individidualPaymentModeVal?.errorCode as keyof typeof errorMappings
                          ]
                        }
                        errorStatus={
                          validatePaymentModeInputFields(
                            paymentModeInputNameType.PERCENTAGE,
                            paymentConfigConcessions?.concessionAmount as number
                          )?.start && formErrors?.individidualPaymentModeVal?.status
                        }
                        handleFocus={() =>
                          setFormErrors({ ...formErrors, individidualPaymentModeVal: defaultErrorState })
                        }
                        textFieldId='concession-input'
                        label={paymentModeOfferInput?.label}
                        {...(paymentModeOfferInput?.startIconComponent
                          ? { startIcon: paymentModeOfferInput?.startIconComponent }
                          : {})}
                        {...(paymentModeOfferInput?.endIconComponent
                          ? { endIcon: paymentModeOfferInput?.endIconComponent }
                          : {})}
                        inputValue={paymentConfigConcessions?.concessionAmount}
                        inputProps={{
                          min: 0,
                          max: offerValueConfig?.valueType === 'FLAT' ? 999999 : 100
                        }}
                        handleKeyDown={disablePlusMinusEKeys}
                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          setFormState(
                            produce((draft: any) => {
                              draft.setup.applicationConfig.offerValueConfig.paymentModeConcessions.find(
                                (mode: checklistItemsType) => mode.valueType === id
                              ).concessionAmount = getValueInRange(
                                event.target.value,
                                offerValueConfig?.valueType === 'FLAT' ? 999999 : 100,
                                0
                              )
                            })
                          )
                        }}
                        inputType={'number'}
                        inputModeType={'tel'}
                      />
                    </div>
                    <span
                      className={`text-sm text-coal-light ${
                        !Boolean(offerValueConfig?.valueType === 'PERCENTAGE_CAPPED') ? 'hidden' : ''
                      }`}
                    >
                      upto
                    </span>
                    <div
                      className={`flex ${
                        !Boolean(offerValueConfig?.valueType === 'PERCENTAGE_CAPPED') ? 'hidden' : ''
                      } w-full`}
                    >
                      <InputTextField
                        errorMessage={
                          errorMappings?.[
                            formErrors?.individidualPaymentModeValUpto?.errorCode as keyof typeof errorMappings
                          ]
                        }
                        errorStatus={
                          validatePaymentModeInputFields(
                            paymentModeInputNameType.UPTO,
                            paymentConfigConcessions?.maxConcessionAmount as number
                          )?.end && formErrors?.individidualPaymentModeValUpto?.status
                        }
                        handleFocus={() =>
                          setFormErrors({ ...formErrors, individidualPaymentModeVal: defaultErrorState })
                        }
                        textFieldId='max-concession-input'
                        label={paymentModeCapInput?.label}
                        {...(paymentModeCapInput?.startIconComponent
                          ? { startIcon: paymentModeCapInput?.startIconComponent }
                          : {})}
                        inputValue={paymentConfigConcessions?.maxConcessionAmount}
                        inputProps={{ min: 0, max: 999999 }}
                        handleKeyDown={disablePlusMinusEKeys}
                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          setFormState(
                            produce((draft: any) => {
                              draft.setup.applicationConfig.offerValueConfig.paymentModeConcessions.find(
                                (mode: checklistItemsType) => mode.valueType === id
                              ).maxConcessionAmount = getValueInRange(event.target.value, 999999, 0)
                            })
                          )
                        }}
                        inputType={'number'}
                        inputModeType={'tel'}
                      />
                    </div>
                  </div>
                </div>
              </ListItem>
            )
          })}
      </List>
    )
  }

  const getIconComponent = (requiredIcon: any) => {
    switch (requiredIcon) {
      case '₹':
        return <>₹</>
      case '%':
        return <>%</>
      default:
        const IconComponent = Icon[requiredIcon as FeatherIconType]
        return <IconComponent className='scale-75' />
    }
  }

  const offerValueC2pTile = () => {
    const c2pRadioItemConfigOptions: RadioItemConfigType[] = [
      {
        label: (
          <div className='flex flex-col gap-2'>
            <div>{t('percentage_off_upto_capped_discount')}</div>
            {offerValueConfig?.valueType === 'PERCENTAGE_CAPPED' && (
              <div className='flex flex-row gap-2 w-[248px]'>
                <InputTextField
                  errorStatus={formErrors?.offerValue?.status}
                  errorMessage={errorMappings?.[formErrors?.offerValue?.errorCode as keyof typeof errorMappings]}
                  handleFocus={() => setFormErrors({ ...formErrors, offerValue: defaultErrorState })}
                  inputType={'number'}
                  inputModeType={'tel'}
                  textFieldId='percentage off '
                  label={t('percentage_off')}
                  inputValue={offerValueConfig?.value}
                  endIcon={<>%</>}
                  handleChange={event => {
                    setFormState(
                      produce((draft: any) => {
                        ;(draft.setup.applicationConfig.offerValueConfig.value = getValueInRange(
                          event?.target.value,
                          100,
                          0
                        )),
                          (draft.setup.applicationConfig.offerValueConfig.capCheckboxConfig.valueType = false)
                      })
                    )
                  }}
                />
                <InputTextField
                  errorStatus={formErrors?.offerLimit?.status}
                  errorMessage={errorMappings?.[formErrors?.offerLimit?.errorCode as keyof typeof errorMappings]}
                  handleFocus={() => setFormErrors({ ...formErrors, offerLimit: defaultErrorState })}
                  inputType={'number'}
                  inputModeType={'tel'}
                  textFieldId='percentage off'
                  label='Percentage off upto'
                  inputValue={offerValueConfig?.cappedValue}
                  startIcon={<>₹</>}
                  handleChange={event => {
                    setFormState(
                      produce((draft: any) => {
                        draft.setup.applicationConfig.offerValueConfig.cappedValue = getValueInRange(
                          event?.target.value,
                          99999,
                          0
                        )
                      })
                    )
                  }}
                />
              </div>
            )}
          </div>
        ),
        valueType: 'PERCENTAGE_CAPPED'
      },
      {
        label: (
          <div className='flex flex-col gap-2'>
            <h2>{t('percentage_off')}</h2>
            {offerValueConfig?.valueType === 'PERCENTAGE' && (
              <div className='flex flex-row w-[120px]'>
                <InputTextField
                  errorStatus={formErrors?.offerValue?.status}
                  errorMessage={errorMappings?.[formErrors?.offerValue?.errorCode as keyof typeof errorMappings]}
                  handleFocus={() => setFormErrors({ ...formErrors, offerValue: defaultErrorState })}
                  inputType={'number'}
                  inputModeType={'tel'}
                  textFieldId='percentage off'
                  label={t('percentage_off')}
                  inputValue={offerValueConfig?.value}
                  endIcon={<>%</>}
                  handleChange={event => {
                    setFormState(
                      produce((draft: any) => {
                        ;(draft.setup.applicationConfig.offerValueConfig.value = getValueInRange(
                          event?.target.value,
                          100,
                          0
                        )),
                          (draft.setup.applicationConfig.offerValueConfig.capCheckboxConfig.valueType = false)
                      })
                    )
                  }}
                />
              </div>
            )}
          </div>
        ),
        valueType: 'PERCENTAGE'
      },
      {
        label: (
          <div className='flex flex-col gap-2'>
            <h2>{t('fixed_amount')}</h2>
            {offerValueConfig?.valueType === 'FLAT' && (
              <div className='flex flex-row w-[120px]'>
                <InputTextField
                  errorStatus={formErrors?.offerValue?.status}
                  errorMessage={errorMappings?.[formErrors?.offerValue?.errorCode as keyof typeof errorMappings]}
                  handleFocus={() => setFormErrors({ ...formErrors, offerValue: defaultErrorState })}
                  inputType={'number'}
                  inputModeType={'tel'}
                  textFieldId='percentage off'
                  label={t('fixed_amount')}
                  inputValue={offerValueConfig?.value}
                  startIcon={<>₹</>}
                  handleChange={event => {
                    setFormState(
                      produce((draft: any) => {
                        ;(draft.setup.applicationConfig.offerValueConfig.value = getValueInRange(
                          event?.target.value,
                          99999,
                          0
                        )),
                          (draft.setup.applicationConfig.offerValueConfig.capCheckboxConfig.valueType = false)
                      })
                    )
                  }}
                />
              </div>
            )}
          </div>
        ),
        valueType: 'FLAT'
      },
      {
        label: (
          <div className='flex flex-col gap-2'>
            <h2>Add a free item (Freebie)</h2>
          </div>
        ),
        valueType: 'FREEBIE'
      }
    ]
    return (
      <div className='flex flex-col gap-3'>
        <FormTile title={t('set_discount_type')}>
          <div className='flex flex-col w-full gap-3'>
            <FloRadioGroup
              selectedOption={
                offerValueConfig?.valueType === 'PERCENTAGE' && offerValueConfig?.capCheckboxConfig?.valueType
                  ? 'PERCENTAGE_CAPPED'
                  : offerValueConfig?.valueType
              }
              options={c2pRadioItemConfigOptions}
              groupName={'c2p-offer-value-type'}
              customClassName='pt-1 text-#4D4D4D text-sm font-normal'
              setSelectedOption={(selectedOption: any) => {
                setFormErrors({
                  ...formErrors,
                  identifier: defaultErrorState,
                  offerValue: defaultErrorState,
                  offerLimit: defaultErrorState
                })
                setFormState(
                  produce((draft: any) => {
                    if (selectedOption === 'FREEBIE') {
                      draft.setup.applicationConfig.entitlementsConfig.valueType = 'PRODUCT'
                      draft.setup.applicationConfig.offerValueConfig.paymentModeCoverageType = 'ALL'
                      draft.setup.applicationConfig.offerValueConfig.value = 100
                      draft.setup.applicationConfig.offerValueConfig.cappedValue = 0
                      draft.setup.applicationConfig.entitlementsConfig.quantityDistributorConfig = [
                        { quantity: 1, itemsConfig: [] }
                      ]
                    } else {
                      draft.setup.applicationConfig.offerValueConfig.value = ''
                      draft.setup.applicationConfig.valueType = 'AMOUNT'
                      draft.setup.applicationConfig.offerValueConfig.cappedValue = ''
                      draft.setup.applicationConfig.entitlementsConfig.quantityDistributorConfig = []
                    }
                    ;(draft.setup.applicationConfig.offerValueConfig.capCheckboxConfig.valueType =
                      selectedOption === 'PERCENTAGE_CAPPED'),
                      (draft.setup.applicationConfig.offerValueConfig.valueType = selectedOption)
                  })
                )
              }}
            />
          </div>
        </FormTile>
        {offerValueConfig?.valueType === 'FREEBIE' && (
          <FormTile title='Select freebie' id='c2p-freebie-tile'>
            <QuantityDistributor
              catalogueCatagory={'PRODUCT_AND_VARIANT'}
              blocksData={entitlementsConfig?.quantityDistributorConfig}
              setFormState={setFormState}
              uiConfig={entitledItemsUIConfig}
              catalogueApplicationType={'ENTITLED'}
              blockTitle='SELECT FREEBIE ITEMS'
              formErrors={formErrors}
              setFormErrors={setFormErrors}
              includeBundleProducts={true}
            />
            {Boolean(formErrors?.entitledQuantity?.status) && (
              <span className='text-sm text-[#CB2711] pl-2'>
                {errorMappings?.[formErrors?.entitledQuantity?.errorCode as keyof typeof errorMappings]}
              </span>
            )}
          </FormTile>
        )}
      </div>
    )
  }
  const renderOfferValueTile = () => {
    return (
      <div
        className={`flex flex-col ${
          applicationConfig?.valueType !== 'BUNDLE' && applicationConfig?.valueType !== 'BXGY' ? 'gap-4' : ''
        } `}
      >
        <FormTile title={offerValueUIConfig?.title} id='offer-value-tile'>
          <div className='flex flex-col w-full gap-3' id='offer-value-tile-container'>
            <div className={`${offerValueUIConfig?.offerValueRadioGroup?.isHidden ? 'hidden' : ''}`}>
              <FloRadioGroup
                selectedOption={offerValueConfig?.valueType}
                options={offerValueUIConfig?.offerValueRadioGroup?.radioItemsUIConfig}
                groupName={'offer-value-type'}
                setSelectedOption={(selectedOption: any) => {
                  setFormErrors({ ...formErrors, offerValue: defaultErrorState, identifier: defaultErrorState })
                  setFormState(
                    produce((draft: any) => {
                      draft.setup.applicationConfig.offerValueConfig.valueType = selectedOption
                      if (selectedOption === 'FREEBIE') {
                        draft.setup.applicationConfig.entitlementsConfig.valueType = 'PRODUCT'
                        draft.setup.applicationConfig.offerValueConfig.paymentModeCoverageType = 'ALL'
                        draft.setup.applicationConfig.offerValueConfig.value = 100
                        draft.setup.applicationConfig.offerValueConfig.cappedValue = 0
                        draft.setup.applicationConfig.entitlementsConfig.quantityDistributorConfig = [
                          { quantity: 1, itemsConfig: [] }
                        ]
                      }
                      if (selectedOption !== 'FREEBIE') {
                        if (applicationConfig?.valueType === 'PREPAID') {
                          draft.setup.applicationConfig.entitlementsConfig.quantityDistributorConfig = []
                          draft.setup.applicationConfig.offerValueConfig.value = 0
                          draft.setup.applicationConfig.offerValueConfig.cappedValue = 0
                          draft.setup.applicationConfig.entitlementsConfig.valueType = 'ALL'
                        }
                      }
                    })
                  )
                }}
              />
            </div>
            {applicationConfig?.valueType !== 'PREPAID' ? renderOfferInputSection() : <></>}
            <div
              className={`${
                typeof capCheckboxUIConfig === 'undefined' || capCheckboxUIConfig?.isHidden ? 'hidden' : ''
              }`}
            >
              <FloCheckbox
                value={Boolean(offerValueConfig?.capCheckboxConfig?.valueType)}
                label={capCheckboxUIConfig?.label}
                setValue={(checked: boolean) => {
                  setFormState(
                    produce((draft: any) => {
                      draft.setup.applicationConfig.offerValueConfig.capCheckboxConfig.valueType = checked
                    })
                  )
                }}
              />
            </div>
            <div className={`${codCheckboxUIConfig?.isHidden ? 'hidden' : ''}`}>
              <FloCheckbox
                value={Boolean(offerValueConfig?.isCodAccepted)}
                label={codCheckboxUIConfig?.label}
                setValue={(checked: boolean) => {
                  setFormState(
                    produce((draft: any) => {
                      draft.setup.applicationConfig.offerValueConfig.isCodAccepted = checked
                    })
                  )
                }}
              />
            </div>
          </div>
        </FormTile>
        <div className={`${offerValueUIConfig?.paymentModeCoverageRadioGroup?.isHidden ? 'hidden' : ''}`}>
          {renderPaymentOfferValueChecklist()}
        </div>
        <div
          id='entitled-quantity-selector'
          className={`${entitlementUIConfig?.isHidden || entitlementsConfig?.valueType === 'ALL' ? 'hidden' : ''}`}
        >
          {renderEntitledQuantitySelectorTile()}
        </div>
      </div>
    )
  }

  const renderPaymentOfferValueChecklist = () => {
    const paymentModeCoverageRadioGroupItems = offerValueUIConfig?.paymentModeCoverageRadioGroup?.radioItemsUIConfig
    return (
      <div className='flex flex-col space-y-4'>
        <div
          className={`${
            entitlementsConfig?.valueType === 'ALL' || offerValueConfig?.valueType !== 'FREEBIE' ? 'hidden' : ''
          }`}
        >
          <FormTile title='Select freebie'>
            <>
              <QuantityDistributor
                catalogueCatagory={'PRODUCT_AND_VARIANT'}
                blocksData={entitlementsConfig?.quantityDistributorConfig}
                setFormState={setFormState}
                uiConfig={entitledItemsUIConfig}
                catalogueApplicationType={'ENTITLED'}
                blockTitle='SELECT FREEBIE ITEMS'
                formErrors={formErrors}
                setFormErrors={setFormErrors}
              />
              {Boolean(formErrors?.entitledQuantity?.status) && (
                <span className='text-sm text-[#CB2711] pl-2'>
                  {errorMappings?.[formErrors?.entitledQuantity?.errorCode as keyof typeof errorMappings]}
                </span>
              )}
            </>
          </FormTile>
        </div>
        <FormTile title='Payment modes for this offer'>
          <>
            <FloRadioGroup
              selectedOption={offerValueConfig?.paymentModeCoverageType}
              options={paymentModeCoverageRadioGroupItems}
              groupName={'payment-mode-coverage-type'}
              setSelectedOption={(selectedOption: any) => {
                setFormErrors({ ...formErrors, offerValue: defaultErrorState })
                setFormState(
                  produce((draft: any) => {
                    draft.setup.applicationConfig.offerValueConfig.paymentModeCoverageType = selectedOption
                  })
                )
              }}
            />
            {Boolean(formErrors?.paymentModeChecklist?.status) &&
              offerValueConfig?.paymentModeCoverageType !== 'ALL' && (
                <span className='text-sm text-[#CB2711] pl-2'>
                  {errorMappings?.[formErrors?.paymentModeChecklist?.errorCode as keyof typeof errorMappings]}
                </span>
              )}
            {(offerValueConfig?.valueType !== 'FREEBIE' ||
              offerValueConfig?.paymentModeCoverageType === 'SPECIFIC') && (
              <>
                {applicationConfig?.valueType === 'PREPAID' && offerValueConfig?.paymentModeCoverageType === 'ALL'
                  ? renderOfferInputSection()
                  : renderPaymentOfferChecklist()}
              </>
            )}
          </>
        </FormTile>
      </div>
    )
  }

  const renderEntitledProductsTile = () => {
    return (
      <FormTile title={entitlementUIConfig?.title}>
        <div className='flex flex-col w-full gap-3'>
          <div className={`${entitlementUIConfig?.entitlementRadioGroup?.isHidden ? 'hidden' : ''}`}>
            <FloRadioGroup
              selectedOption={entitlementsConfig?.valueType}
              options={entitlementUIConfig?.entitlementRadioGroup?.radioItemsUIConfig}
              groupName={'entitled-catalogue'}
              setSelectedOption={(selectedOption: any) => {
                setFormErrors({
                  ...formErrors,
                  identifier: defaultErrorState,
                  entitledProducts: defaultErrorState,
                  offerValue: defaultErrorState,
                  quantityDistributor: defaultQuantityDistributorErrorState,
                  quantityDistributorProduct: defaultQuantityDistributorErrorState
                })
                setFormState(
                  produce((draft: any) => {
                    draft.setup.applicationConfig.entitlementsConfig.valueType = selectedOption
                    if (applicationConfig?.valueType === 'AMOUNT') {
                      draft.setup.applicationConfig.entitlementsConfig.setQuantityCheckboxConfig.valueType = false
                    }
                    draft.setup.applicationConfig.entitlementsConfig.quantityDistributorConfig = [
                      { quantity: applicationConfig?.valueType === 'AMOUNT' ? null : 1, itemsConfig: [] }
                    ]
                  })
                )
              }}
            />
          </div>
        </div>
      </FormTile>
    )
  }

  const renderBulkGenerationTile = () => {
    return (
      <FormTile title={discountGenerationUIConfig?.title}>
        <div className='flex flex-col w-full gap-3'>
          <div className={`${discountGenerationUIConfig?.generationRadioGroup?.isHidden ? 'hidden' : ''}`}>
            <FloRadioGroup
              isDisabled={isEditMode}
              selectedOption={discountGenerationConfig?.bulkGenerationConfig?.valueType}
              options={discountGenerationUIConfig?.generationRadioGroup?.radioItemsUIConfig}
              groupName={'bulk-generation-type'}
              setSelectedOption={(selectedOption: any) => {
                setFormErrors({ ...formErrors, codeCount: defaultErrorState, codeLength: defaultErrorState })
                setFormState(
                  produce((draft: any) => {
                    draft.setup.applicationConfig.discountGenerationConfig.bulkGenerationConfig.valueType =
                      selectedOption
                  })
                )
              }}
            />
          </div>
          <div className={`${bulkGenerationUIConfig?.valueType === 'RANDOM_CODES' ? 'flex' : 'hidden'} flex-col gap-3`}>
            <div className='flex w-2/3 gap-2'>
              <div className='w-1/2'>
                <InputTextField
                  isDisabled={isEditMode}
                  errorStatus={formErrors?.codeCount?.status}
                  errorMessage={errorMappings?.[formErrors?.codeCount?.errorCode as keyof typeof errorMappings]}
                  textFieldId='code-count-input'
                  label={bulkGenerationUIConfig?.codeCountInput?.label}
                  inputValue={discountGenerationConfig?.bulkGenerationConfig?.codeCount}
                  inputProps={{ min: 0, max: 99999999 }}
                  handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFormState(
                      produce((draft: any) => {
                        draft.setup.applicationConfig.discountGenerationConfig.bulkGenerationConfig.codeCount =
                          getValueInRange(event.target.value, 99999999, 0, true)
                      })
                    )
                  }}
                  inputType={'number'}
                  inputModeType={'tel'}
                  handleFocus={() => setFormErrors({ ...formErrors, codeCount: defaultErrorState })}
                />
              </div>
              <div className={`w-1/2`}>
                <InputTextField
                  isDisabled={isEditMode}
                  errorStatus={formErrors?.codeLength?.status}
                  errorMessage={errorMappings?.[formErrors?.codeLength?.errorCode as keyof typeof errorMappings]}
                  textFieldId='code-length-input'
                  label={bulkGenerationUIConfig?.codeLengthInput?.label}
                  inputValue={discountGenerationConfig?.bulkGenerationConfig?.codeLength}
                  inputProps={{ min: 0, max: 999999 }}
                  handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFormState(
                      produce((draft: any) => {
                        draft.setup.applicationConfig.discountGenerationConfig.bulkGenerationConfig.codeLength =
                          getValueInRange(event.target.value, 999999, 0, true)
                      })
                    )
                  }}
                  inputType={'number'}
                  inputModeType={'tel'}
                  handleFocus={() => setFormErrors({ ...formErrors, codeLength: defaultErrorState })}
                />
              </div>
            </div>
            <div className='flex w-2/3 gap-2'>
              <div className='w-1/2'>
                <InputTextField
                  isDisabled={isEditMode}
                  textFieldId='prefix-input'
                  label={bulkGenerationUIConfig?.prefixInput?.label}
                  inputValue={discountGenerationConfig?.bulkGenerationConfig?.prefix}
                  inputProps={{ maxLength: 16 }}
                  handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFormState(
                      produce((draft: any) => {
                        draft.setup.applicationConfig.discountGenerationConfig.bulkGenerationConfig.prefix =
                          event.target.value.trim().toUpperCase()
                      })
                    )
                  }}
                />
              </div>
              <div className={`w-1/2`}>
                <InputTextField
                  isDisabled={isEditMode}
                  textFieldId='suffix-input'
                  label={bulkGenerationUIConfig?.suffixInput?.label}
                  inputValue={discountGenerationConfig?.bulkGenerationConfig?.suffix}
                  inputProps={{ maxLength: 16 }}
                  handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFormState(
                      produce((draft: any) => {
                        draft.setup.applicationConfig.discountGenerationConfig.bulkGenerationConfig.suffix =
                          event.target.value.trim().toUpperCase()
                      })
                    )
                  }}
                />
              </div>
            </div>
            <div className='flex flex-col w-full gap-2 border-t-[1px] border-gray-100'>
              <Typography variant='body1' sx={{ fontWeight: 500, color: 'text.header', marginTop: '0.5rem' }}>
                {bulkGenerationUIConfig?.allowedCharactersTitle}
              </Typography>
              <FloRadioGroup
                isDisabled={isEditMode}
                selectedOption={discountGenerationConfig?.bulkGenerationConfig?.allowedCharacters}
                options={bulkGenerationUIConfig?.allowedCharactersRadioGroup?.radioItemsUIConfig}
                groupName={'allow-characters'}
                setSelectedOption={(selectedOption: any) =>
                  setFormState(
                    produce((draft: any) => {
                      draft.setup.applicationConfig.discountGenerationConfig.bulkGenerationConfig.allowedCharacters =
                        selectedOption
                    })
                  )
                }
              />
            </div>
          </div>
          <div className={`${bulkGenerationUIConfig?.valueType === 'CSV_UPLOAD' ? '' : 'hidden'}`}>
            {/* <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            <FileUploader
                                buttonText={bulkGenerationUIConfig?.uploadCSVUIConfig?.uploadButton?.text ?? ""}
                                type='.csv'
                                uploadLink={`issuer/${accountId}/merchant/${accountId}/coupon/bulk/file`}
                                size='medium'
                                variant='outlined'
                                color='primary'
                                acceptedSize={104857600}
                                baseEndpoint={apiURI.DISCOUNT_ENGINE}
                                disabled={isEditMode}
                                uploadOnFileSave={false}
                                setSelectedCSVFormData={(value: any) => {
                                    setFormErrors({ ...formErrors, codeCSV: defaultErrorState });
                                    setFormState(produce((draft: any) => {
                                    }));
                                }}
                                setSelectedCSVPath={(value: any) => {
                                    setFormState(produce((draft: any) => {
                                        draft.setup.applicationConfig.discountGenerationConfig.bulkGenerationConfig.uploadedFilePath = value;
                                    }));
                                }}
                            />
                            <div className={`${!Boolean(discountGenerationConfig?.bulkGenerationConfig?.uploadedFilePath) ? 'hidden' : ''} flex items-center gap-1`}>
                                <Icon.File className='scale-75 stroke-2'/>
                                <Typography variant='subtitle2' sx={{ marginTop: 1, color: "text.primary" }}>
                                    {discountGenerationConfig?.bulkGenerationConfig?.uploadedFilePath}
                                </Typography>
                            </div> 
                        </div> */}

            <div className='flex flex-col gap-2 bg-[#FAFAFA] p-4 rounded-lg'>
              <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.header' }}>
                Sample CSV file format:
              </Typography>
              <CSVPreview
                rows={[['BULK_DISCOUNT_CODE_2'], ['BULK_DISCOUNT_CODE_3'], ['BULK_DISCOUNT_CODE_4']]}
                columns={['BULK_DISCOUNT_CODE_1']}
              />
              <FileUploaderWithActions
                buttonText={bulkGenerationUIConfig?.uploadCSVUIConfig?.uploadButton?.text ?? ''}
                type='.csv'
                uploadLink='/attributes/v1/files/upload'
                size='medium'
                variant='outlined'
                disabled={isEditMode}
                color='primary'
                acceptedSize={104857600}
                uploadOnFileSave={true}
                placeholderText={
                  <p className={isEditMode ? 'text-gray-500' : ''}>
                    Upload specific codes with usage limits
                    <br />
                    <span className='text-sm text-[#949494]'>
                      If usage limit is not specified for a code, it will be limited to one use
                    </span>
                  </p>
                }
                setUploadedFileData={fileData => {
                  setFormState(
                    produce((draft: any) => {
                      draft.setup.applicationConfig.discountGenerationConfig.bulkGenerationConfig.uploadedFileFormData =
                        fileData
                      draft.setup.applicationConfig.discountGenerationConfig.bulkGenerationConfig.uploadedFilePath =
                        fileData.url
                    })
                  )
                }}
                prevUploadedFileName={discountGenerationConfig?.bulkGenerationConfig?.uploadedFilePath}
                errorsToCheck={[ErrorType.FILE_SIZE_EXCEEDED, ErrorType.DISCOUNT_INVALID_CODE]}
              />
              <div className=' bg-[#C7981F1A] flex flex-row justify-center items-center gap-1 w-fit p-2 rounded-lg border-yellow-400 border-1 '>
                <Info size={16} className='pr-1' color='#C7981F' />
                <p className='text-sm font-normal text-coal-light'>
                  Please ensure the uploaded file is in the right format.
                </p>
                <a
                  className='text-sm font-semibold underline text-coal-dark decoration-gray-light decoration-[1.5px] underline-offset-4 w-fit'
                  download
                  href={bulkGenerationUIConfig?.uploadCSVUIConfig?.sampleDownloadLink?.link}
                >
                  Download a sample file
                </a>
              </div>
            </div>
            {formErrors?.codeCSV?.status ? (
              <TextFieldError message={errorMappings?.[formErrors?.codeCSV?.errorCode as keyof typeof errorMappings]} />
            ) : (
              <></>
            )}
          </div>
        </div>
      </FormTile>
    )
  }

  const renderEntitledQuantitySelectorTile = () => {
    let aggregatedQuantity = 0
    entitlementsConfig?.quantityDistributorConfig?.forEach((block: QuantityDiscountorBlockConfigType) => {
      if (block?.quantity) aggregatedQuantity += block?.quantity
    })
    return (
      <div
        className={`flex flex-col gap-3 p-4 bg-white border rounded border-gray-light ${
          applicationConfig?.valueType !== 'BUNDLE' && applicationConfig?.valueType !== 'BXGY'
            ? ''
            : '-mt-6 rounded-t-none border-t-0'
        }`}
      >
        {applicationConfig?.valueType !== 'BUNDLE' && applicationConfig?.valueType !== 'BXGY' && (
          <Typography
            variant='subtitle1'
            sx={{ fontWeight: 500, color: 'text.header', display: 'flex', alignItems: 'center' }}
          >
            {'Choose items to discount'}
          </Typography>
        )}
        {Boolean(entitlementsConfig?.valueType === 'PRODUCT' || entitlementsConfig?.valueType === 'COLLECTION') ? (
          <>
            {applicationConfig?.valueType === 'AMOUNT' && (
              <div className='flex flex-col gap-2' id='offer-value-entitled-quantity-container'>
                <FloCheckbox
                  value={Boolean(entitlementsConfig?.setQuantityCheckboxConfig?.valueType)}
                  label={'Set quantity of items to discount'}
                  setValue={(checked: boolean) => {
                    const blockData = createDeepCopy<QuantityDiscountorBlockConfigType[] | undefined>(
                      entitlementsConfig?.quantityDistributorConfig
                    ) as QuantityDiscountorBlockConfigType[]
                    setFormState(
                      produce((draft: any) => {
                        draft.setup.applicationConfig.entitlementsConfig.setQuantityCheckboxConfig.valueType = checked
                        draft.setup.applicationConfig.entitlementsConfig.quantityDistributorConfig =
                          getQuantityDistributerDefaultConfig(checked, blockData)
                      })
                    )
                  }}
                />
                {Boolean(entitlementsConfig?.setQuantityCheckboxConfig?.valueType) && (
                  <InputTextField
                    customClass='bg-[#FFF] rounded-lg !w-40 pl-6'
                    textFieldId={`aggregated-quantity-input`}
                    inputValue={aggregatedQuantity}
                    isDisabled={true}
                    inputType={'number'}
                    inputModeType={'tel'}
                  />
                )}
                {Boolean(formErrors?.entitledQuantity?.status) && (
                  <span className='text-sm text-[#CB2711] '>
                    {errorMappings?.[formErrors?.entitledQuantity?.errorCode as keyof typeof errorMappings]}
                  </span>
                )}
              </div>
            )}
            <QuantityDistributor
              id='offer-value-entitled-quantity-distributor'
              catalogueCatagory={entitledCatalogueCatagory}
              blocksData={entitlementsConfig?.quantityDistributorConfig}
              setFormState={setFormState}
              uiConfig={entitledItemsUIConfig}
              catalogueApplicationType={'ENTITLED'}
              formErrors={formErrors}
              setFormErrors={setFormErrors}
              hideAddButton={
                applicationConfig?.valueType === 'AMOUNT' &&
                !Boolean(entitlementsConfig?.setQuantityCheckboxConfig?.valueType)
              }
              includeBundleProducts={true}
            />
          </>
        ) : (
          <></>
        )}
      </div>
    )
  }

  return (
    <>
      {/* Step distribution */}
      <div id='title-section' className={`${titleSectionUIConfig?.isHidden ? 'hidden' : ''}`}>
        {renderTitleInputSection()}
      </div>

      <div id='description-section' className={`${descriptionSectionUIConfig?.isHidden ? 'hidden' : ''}`}>
        {renderDescriptionInputSection()}
      </div>

      <div id='application-method' className={`${applicationMethodUIConfig?.isHidden ? 'hidden' : ''}`}>
        {renderApplicationMethodTile()}
      </div>

      <div id='generation' className={`${discountGenerationUIConfig?.isHidden ? 'hidden' : ''}`}>
        {renderBulkGenerationTile()}
      </div>

      <div id='application' className={`${applicationUIConfig?.isHidden ? 'hidden' : ''}`}>
        {renderApplicationTile()}
      </div>

      <div
        id='entitled-products'
        className={`${entitlementUIConfig?.isHidden || applicationStage === 'POST_ORDER_PLACEMENT' ? 'hidden' : ''}`}
      >
        {renderEntitledProductsTile()}
      </div>
      <div
        id='offer-value'
        className={`${offerValueUIConfig?.isHidden || applicationStage === 'POST_ORDER_PLACEMENT' ? 'hidden' : ''}`}
      >
        {renderOfferValueTile()}
      </div>
      <div id='c2p-offer-value' className={`${applicationStage !== 'POST_ORDER_PLACEMENT' ? 'hidden' : ''}`}>
        {offerValueC2pTile()}
      </div>
    </>
  )
}

export default DiscountSetupForm
